import React from 'react';
import { Box, Heading, Link, Text } from '@chakra-ui/react';

export const PrivacyPolicyContent = () => {
  return (
    <Box p={4} maxW="container.lg">
      <Heading as="h1" size="lg" mb={4}>
        Schneider Electric Data Privacy Policy
      </Heading>

      <Heading as="h2" size="md" mt={4} mb={2}>
        1. Changes to this Privacy Policy
      </Heading>
      <Text mb={2}>
        Schneider Electric may modify this Privacy Policy as needed. Schneider Electric reserves the right to modify
        this Privacy Policy as needed, for example to comply with evolutions in laws, regulations, Schneider Electric
        practices and procedures, or requests by supervisory authorities.
      </Text>
      <Text mb={2}>
        In this case, Schneider Electric will post changes to our Privacy Policy on this page. Please check this page
        regularly to keep up-to-date.
      </Text>

      <Heading as="h2" size="md" mt={4} mb={2}>
        2. Why this Privacy Policy?
      </Heading>
      <Text mb={2}>
        Schneider Electric is committed to protecting your privacy and personal information. This Privacy Policy informs
        you of our privacy practices and of how personal information is protected. You should read it before accessing
        Schneider Electric digital content.
      </Text>
      <Text mb={2}>
        Schneider Electric strongly supports the fundamental rights to privacy and data protection as well as compliance
        with national and international privacy laws, in particular the Australian Information Privacy Principles.
      </Text>
      <Text mb={2}>
        This Privacy Policy describes how Schneider Electric processes and protects personal information of individuals
        who use our websites and other digital platforms as well as in the context of our offline business activities.
      </Text>
      <Text mb={2}>
        Schneider Electric has adopted an internal Global Data Privacy Policy which is applicable to all our
        subsidiaries for the collection, processing, use, dissemination, transfer and storage of personal information.
        It imposes common rules for all of our subsidiaries of all countries and aims at ensuring a high level of
        protection of personal information within Schneider Electric. In addition to complying with Australian privacy
        laws, we respect and take into account the major principles of EU data protection rules as one of our Head
        Offices is located in the European Union.
      </Text>

      <Heading as="h2" size="md" mt={4} mb={2}>
        3. What is the scope of this Privacy Policy? Who is in charge of data processing?
      </Heading>
      <Text mb={2}>
        This Privacy Policy applies to all Schneider Electric subsidiaries and digital content, unless a specific
        privacy notice has been released to supplement it or to replace it. You should check the privacy notices made
        available to you on each digital platform such as websites or applications.
      </Text>
      <Text mb={2}>
        Schneider Electric is a global company, with legal entities, business processes, management organisations, and
        system infrastructure that cross borders. This Privacy Policy applies to all Schneider Electric data processing
        activities run by our controlled subsidiaries, including product and service offerings and digital content (e.g.
        event registrations, websites, applications, tutorials, e-trainings, newsletters, advertising, and other
        communication). It may however be supplemented by a more specific privacy notice/statement/policy or even
        superseded by another policy, specific to a particular Schneider Electric programme, product, service, content
        or entity. It is important that you read the statements made available to you for your full information. For
        instance, the processing of job candidate applications is governed by the privacy notice posted on our Career
        Portal.
      </Text>
      <Text mb={2}>
        The data controllers of the data processing activities are the Schneider Electric subsidiaries who have
        determined the data processing means and purposes. They may vary on a case-by-case basis. In many instances,
        Schneider Electric Industries SAS, 35 rue Joseph Monier 92500 Rueil-Malmaison - France, Schneider Electric's
        Head Office, is the data controller of global data processing activities. For information you can contact us at{' '}
        <Link color={'blue.600'} href="mailto:Global-Data-Privacy@schneider-electric.com">
          Global-Data-Privacy@schneider-electric.com
        </Link>
        .
      </Text>
      <Text mb={2}>This Privacy Policy binds all Schneider Electric controlled subsidiaries, and their employees.</Text>
      <Text mb={2}>
        Any order made by you online is also subject to the terms and conditions provided on the relevant sites. You
        must read them.
      </Text>

      <Heading as="h2" size="md" mt={4} mb={2}>
        4. Why do we collect and use personal information?
      </Heading>
      <Text mb={2}>
        We use personal information for various purposes, including to fulfil orders and requests, to manage customer
        and prospect relationships, conduct surveys, improve our products, services, digital content as well as user
        journeys, manage user accounts and programmes, analyse activities on our digital platforms, run marketing
        activities, provide user with contextual and relevant information, ensure the security of our activities,
        protect against fraud, and, more generally, run our business activities.
      </Text>
      <Text mb={2}>
        Our primary goal in collecting information is to provide you with superior service and a smooth, efficient and
        personalised experience while using our digital content.
      </Text>
      <Text mb={2}>Schneider Electric collects personal information for the following purposes:</Text>
      <Text mb={2}>
        • To fulfil orders of product, services and digital content. We collect this information to deliver your order,
        to obtain payment, to provide the functionalities of our applications, to communicate with you about the status
        of your order and for contract management purposes.
      </Text>
      <Text mb={2}>
        • To better address future requests for information and support. We keep record of exchanges including personal
        information to provide an optimised service on contacting us for support.
      </Text>
      <Text mb={2}>
        • For relationship management purposes which includes communicating about the products, services and digital
        content including email communications and messaging.
      </Text>
      <Text mb={2}>
        • To conduct surveys and polls. We may collect personal information to complete surveys and contact you for
        follow-up. We use this information to measure satisfaction, get to know our customers better and improve
        products, services and digital content.
      </Text>
      <Text mb={2}>
        • To improve our products, services and digital content; for instance, we analyse the use of our applications
        and websites in order to identify areas and functionalities where users have difficulties, and we improve them.
      </Text>
      <Text mb={2}>
        • To learn about your browsing and searching activity. We collect navigation information when you visit our
        digital content or click on links for products and services. We collect this information to improve user
        experience for next visits (e.g., country selection or profile-focused content), help our websites run more
        efficiently, to gather broad demographic information to analyse the websites' activity and performance, and to
        evaluate the relevance and effectiveness of our advertising to better serve our customers. We may use cookies
        and other identifiers or similar technologies (referred to hereinafter as “Cookies”) to obtain information on
        the referring website, type of browser used, the content viewed, as well as the date and time of access to the
        digital content. You may read our Cookie Notice for more information on our use of Cookies and your options for
        managing Cookies.
      </Text>
      <Text mb={2}>
        • To manage user digital accounts. We collect personal information at registration on our digital platforms
        (e.g. partner portal, ordering platform, mobile apps) to authenticate you, manage credentials to enable you to
        navigate through several digital platforms without having to re-authenticate (cf. single sign-on) and to
        interact with you.
      </Text>
      <Text mb={2}>
        • To run promotional programmes and activities: we collect personal information when you join a promotional or
        reward programme or activity. We use this information to administer the programme or activity, to send relevant
        e-mails about the programme and activity, notify winners, and make the winners' list publicly available in
        accordance with applicable regulations and laws.
      </Text>
      <Text mb={2}>
        • To send you marketing information by mail, fax, phone, text messages, email and electronic communications
        about promotions, news and new products or services that we think may be of interest to you in compliance with
        applicable opt-in and opt-out requirements. This can be conducted by Schneider Electric, its subsidiaries or
        selected third-parties acting on our behalf.
      </Text>
      <Text mb={2}>
        • To provide you with certain communications and/or targeted information about our products and services (or of
        our subsidiaries) through pop-ups, banners, videos, emails and any other advertising format. For instance, we
        may provide you with contextual advertising or other content, based on browsing information. We may also use
        location data to inform you about local promotions and activities. Please read our Cookie Notice for more
        information on the use of Cookies and your options for managing them.
      </Text>
      <Text mb={2}>
        • To create a user profile that we may use for our sales and marketing operations and to provide you with
        personalised content and services, on the basis of information obtained online from different digital platforms
        and information we collected about you offline considering your cookie preferences (see our Cookie Notice). For
        example, a user that we believe is an electrician (due notably to information provided at the time of
        registration and other information obtained from third-party sources), may see profile related advertising
        and/or on third-party websites and apps that a user with another energy-related specialty would not see.
        Similarly, users may see different content or receive different emails containing different advertisements or
        surveys. We may also use Cookies to geographically target users.
      </Text>
      <Text mb={2}>
        • To ensure the security of our products, services and digital content, of our activities and of others, for the
        protection against fraud and the compliance with our terms and conditions and this Privacy Policy. We may also
        investigate potential breaches.
      </Text>
      <Text mb={2}>
        • To enable or administer our business, such as for quality control and consolidated reporting.
      </Text>
      <Text mb={2}>
        • To support corporate transactions or reorganisations in which Schneider Electric is involved.
      </Text>
      <Text mb={2}>• For business continuity and disaster recovery.</Text>
      <Text mb={2}>
        • To comply with legal obligations to which we are subject. For example, accounting and tax obligations.
      </Text>
      <Text mb={2}>• Any other purpose otherwise conveyed to you.</Text>

      <Heading as="h2" size="md" mt={4} mb={2}>
        5. What type of personal information do we process?
      </Heading>
      <Text mb={2}>
        Schneider Electric processes various types of personal information including identity and contact related
        information, professional related information, information about preferences, interactions with us, financial
        related information, online traffic data and the content you provide to us. In most instances this information
        is obtained from customers, partners and users. We also purchase lists from marketing agencies and obtain
        information from our partners, through Cookies and social networks.
      </Text>
      <Text mb={2}>
        Personal information (also called personal data) is any information relating to an identified or identifiable
        individual. An identifiable individual is one who can be identified, directly or indirectly, in particular by
        reference to an identifier or to one or more factors specific to his physical, physiological, mental, economic,
        cultural or social identity.
      </Text>
      <Text mb={2}>The types of personal information we process about you may include:</Text>
      <Text mb={2}>
        • Identity and contact related information, such as name, company, email address, phone number, photo, age
        range, contact addresses;
      </Text>
      <Text mb={2}>
        • Professional information, such as customer type, purchasing authority, purchasing timeframe; acquisition and
        use of Schneider Electric products, services and digital content;
      </Text>
      <Text mb={2}>• Information (e.g. energy consumption) which is specific to the digital platform you use;</Text>
      <Text mb={2}>
        • Your preferences such as product and service preferences, contact preferences, marketing preferences;
      </Text>
      <Text mb={2}>• Your interactions with us such as queries, orders, claims, survey answers;</Text>
      <Text mb={2}>• Financial-related information such as bank account details, credit card information;</Text>
      <Text mb={2}>
        • Online traffic data such as IP address, device and system identifiers, your Schneider Electric user ID and
        password, referring website, type of browser used, consulted content, and location, based either on your IP
        address or on information transmitted by your mobile device;
      </Text>
      <Text mb={2}>• Content that you have provided via online forums or otherwise.</Text>
      <Text mb={2}>
        This Privacy Policy does not cover personal information rendered anonymous. If anonymised data becomes
        identifiable, then this Privacy Policy shall apply.
      </Text>

      <Heading as="h2" size="md" mt={4} mb={2}>
        6. Your content
      </Heading>
      <Text mb={2}>
        You may choose to contribute content, including photos or comments, to online forums, applications or other
        digital platforms operated by Schneider Electric. Your content must be harmless. It must respect the law, the
        rights and interests of others. You need to have obtained consent before sharing someone else's data. You should
        apply caution before sharing information in a forum.
      </Text>
      <Text mb={2}>
        The content provided to us must respect the rights and interests of others, including their rights to protection
        of personal information and privacy. It should not be offensive, disrespectful or be harmful in any way.
      </Text>
      <Text mb={2}>
        Any provision of personal information to Schneider Electric about another individual must be compliant with
        privacy laws, including with notice and consent requirements for the disclosure of that information.
      </Text>
      <Text mb={2}>
        While Schneider Electric strives to protect your personal information, providing it online on shared forums is
        not risk-free. If you post, comment, indicate interest, or share personal information, including photographs, to
        any forum, social network or blog, please be aware that any personal information you submit can be read, viewed,
        collected, or used by other users of these forums, and could be used to contact you, send you unsolicited
        messages, or for purposes that neither you nor Schneider Electric have control over. Schneider Electric is not
        responsible for the personal information you choose to submit in these forums. You should apply caution before
        deciding to share information about yourself or another person.
      </Text>

      <Heading as="h2" size="md" mt={4} mb={2}>
        7. Who do we share personal information with?
      </Heading>
      <Text mb={2}>
        We share personal information with the Schneider Electric subsidiaries and the service providers involved in our
        activities, with advertising and marketing agencies, with social networks if you use their login credentials,
        with third-party websites if you register with Schneider Electric credentials, competent regulatory bodies and
        authorities and business successors.
      </Text>
      <Text mb={2}>
        Schneider Electric is a global group of companies which works as one. To provide the best service to customers,
        prospects and users, personal data may be shared amongst personnel working for different entities. It may be the
        case, for example, for customer relationship management, sales or product support, marketing, product
        development purposes, improvement of the products, services and digital content, data quality checks, or
        security, finance, regulatory and compliance purposes.
      </Text>
      <Text mb={2}>
        We resort to service providers to carry out data processing activities and to provide our products, services and
        online content to you. These service providers include, without limitation, providers of hosting facilities,
        information systems, advertising and marketing agencies, IT support, security services, financial services,
        carriers who deliver products, outside accounting firms, lawyers and auditors.
      </Text>
      <Text mb={2}>
        We ensure all service providers working under contract for Schneider Electric are compliant with data privacy
        laws and aligned with Schneider Electric guidelines.
      </Text>
      <Text mb={2}>We also share the information as follows:</Text>
      <Text mb={2}>
        • In connection with the provision of advertising, we may share some limited personal information (e.g. device
        identifiers, Cookie identifiers) with ad exchanges or agencies that manage advertising on third-party websites
        and apps on which you may see advertising.
      </Text>
      <Text mb={2}>
        • You may access our digital content and/or register through login credentials of third-party websites (such as
        Facebook and LinkedIn). If you do so, we will be able to access some of the information you registered on these
        social networks, to assist you to pre-fill the registration form for Schneider Electric digital content. By
        using the login credentials of these third-party websites, you may at the same time inform your contacts on
        these sites that you have created an account with Schneider Electric. If you want to know more about the
        information accessible by your contacts on these sites and how to restrict their communication, we encourage you
        to read their terms of use and privacy policies.
      </Text>
      <Text mb={2}>
        • Schneider Electric may disclose your personal information as necessary to potential buyers and successors in
        title, to facilitate a merger, consolidation, transfer of control or other corporate reorganisation in which
        Schneider Electric participates.
      </Text>
      <Text mb={2}>
        • Where required by law or court orders or to protect our legal rights, we will disclose your personal
        information to government agencies, regulators and competent authorities.
      </Text>
      <Text mb={2}>• In other ways described in this Privacy Policy or to which you have otherwise consented.</Text>
      <Text mb={2}>
        • Aggregated with other information, in such a way that your identity cannot reasonably be determined (for
        example, statistical compilations).
      </Text>
      <Text mb={2}>• We will not sell or rent your personal information to a third party without your permission.</Text>

      <Heading as="h2" size="md" mt={4} mb={2}>
        8. How do we hold and protect personal information?
      </Heading>
      <Text mb={2}>
        Schneider Electric complies with widely recognised key data protection principles (fairness, purpose limitation,
        data quality, data retention, compliance with individuals' rights, security) and takes reasonable measures for
        the security of personal information. Schneider Electric may hold your personal information electronically or in
        hard copy.
      </Text>
      <Text mb={2}>
        Schneider Electric respects the privacy rights and interests of individuals. Schneider Electric and its
        subsidiaries observe the following principles when processing personal information:
      </Text>
      <Text mb={2}>• Processing personal information fairly and lawfully;</Text>
      <Text mb={2}>
        • Collecting personal information for specified, legitimate purposes and not processing it further in ways
        incompatible with those purposes;
      </Text>
      <Text mb={2}>
        • Collecting personal information which is relevant to and not excessive for the purposes for which it is
        collected and used. We may render information anonymous when feasible and appropriate, depending on the nature
        of the data and the risks associated with the intended uses;
      </Text>
      <Text mb={2}>
        • Maintaining accurate personal information, and where necessary, kept up-to-date. We will take reasonable steps
        to rectify or delete information that is inaccurate or incomplete;
      </Text>
      <Text mb={2}>
        • Keeping personal information only as long as it is necessary for the purposes for which it was collected and
        processed;
      </Text>
      <Text mb={2}>• Processing personal information in accordance with individuals' legal rights;</Text>
      <Text mb={2}>
        • Taking appropriate technical, physical, and organisational measures to prevent unauthorised access, unlawful
        processing, and unauthorised or accidental loss, destruction, or damage to personal information;
      </Text>
      <Text mb={2}>
        • When processing sensitive personal information, ensuring appropriate notice and consent is obtained or that
        the processing otherwise complies with applicable law;
      </Text>
      <Text mb={2}>
        Schneider Electric and all its subsidiaries must ensure that the above principles are complied with.
      </Text>

      <Text mb={2}>
        Schneider Electric and its subsidiaries are committed to taking commercially reasonable technical, physical, and
        organisational measures to protect personal information against unauthorised access, unlawful processing,
        accidental loss or damage, and unauthorised destruction. We offer the use of secure servers to enable you to
        place orders or to access your account information. We implement access control measures for our internal
        systems that hold personal information. Authorised users are given access to such systems through the use of a
        unique identifier and password. Access to personal information is provided to our staff for the sole purpose of
        performing their job duties. We sensitise our employees on proper use and handling of personal information. We
        also require our service providers to maintain compliant security measures. We implement security measures to
        determine the identity of registered users, so that appropriate rights and restrictions can be enforced for
        these users. In case of a registered user, we use both log ins and passwords for authentication. You are
        responsible for maintaining the security of your log-in credentials.
      </Text>
      <Text mb={2}>
        By using our digital content or providing personal information to us, you agree that we may communicate with you
        electronically or otherwise about related security, privacy, use and administrative activities. In spite of our
        efforts to implement appropriate security measures, online browsing carries inherent risks and we cannot
        guarantee that it is risk-free.
      </Text>
      <Heading as="h2" size="md" mt={4} mb={2}>
        9. Third-party websites and social media
      </Heading>
      <Text mb={2}>
        You should read the privacy policy of third-party websites and social media and adjust privacy settings as you
        see fit. Non-Schneider Electric websites and platforms are not covered by this Privacy Policy.
      </Text>
      <Text mb={2}>
        Schneider Electric digital platforms may provide links to third-party applications, products, services, or
        websites for the user convenience and information. If you access those links, you will leave the Schneider
        Electric digital platform. Schneider Electric does not control those third-party sites or their privacy
        practices, which may differ from Schneider Electric's practices. We do not endorse or make any representations
        about third-party sites. The personal information you choose to provide or that is collected by, these third
        parties are not covered by the Schneider Electric Privacy Policy. We encourage you to review the privacy policy
        of any site you interact with, before allowing the collection and use of your personal information.
      </Text>
      <Text mb={2}>
        We also provide social media links that enable you to share information with your social networks and to
        interact with Schneider Electric on various social media sites. The use of these links may result in the
        collection or sharing of information about you. We encourage you to review the privacy policies and the privacy
        settings of the social media sites with which you interact to make sure you understand the information that may
        be collected, used, and shared by those sites and to adjust these settings as you see fit.
      </Text>

      <Heading as="h2" size="md" mt={4} mb={2}>
        10. What are your rights?
      </Heading>
      <Text mb={2}>
        Schneider Electric will comply with your data protection rights, including your rights to request access to your
        personal information and to request that it be deleted or amended. You can always opt out of any direct
        marketing activity performed by Schneider Electric.
      </Text>
      <Text mb={2}>
        You may enquire about the nature of the personal information stored or processed about you by any Schneider
        Electric entity. You can request access to your personal information and request that it be deleted or amended
        at any time. To exercise your rights please send an email to{' '}
        <Link color={'blue.600'} href="mailto:Global-Data-Privacy@schneider-electric.com">
          Global-Data-Privacy@schneider-electric.com
        </Link>
        .
      </Text>
      <Text mb={2}>
        If we refuse to give you access to or correct the personal information as requested by you, we will provide the
        reasons for the refusal and the mechanisms available should you wish to lodge a complaint. We will always take
        reasonable steps to give access to you or make corrections in a way that meets your needs as well as the needs
        of Schneider Electric.
      </Text>
      <Text mb={2}>
        You always have the right to opt out of our marketing communications. Your prior consent is sought, when
        required by any applicable law. To opt out of marketing emails, simply use the functionality provided at the
        bottom of any email we send.
      </Text>
      <Text fontWeight="bold">Anti-spam obligations</Text>
      <Text mb={2}>
        Schneider Electric is committed to complying with its anti-spam obligations in accordance with the Spam Act
        (Cth). Commercial electronic messages (in most cases, emails) sent to you which contain promotional material
        about services we provide will be sent either with your express or inferred consent. Such messages will always
        contain our contact information and a functional unsubscribe facility to allow you to opt-out of receiving
        messages of this nature from us in the future. We will honour any such unsubscribe requests within five working
        days.
      </Text>

      <Heading as="h2" size="md" mt={4} mb={2}>
        11. Important information for individuals
      </Heading>
      <Text fontWeight="bold">
        11.1 On what legal ground is Schneider Electric relying to use personal information?
      </Text>
      <Text mb={2}>
        The use of personal information is necessary, with respect to the purposes mentioned in section 3 above, to,
        respectively:
      </Text>
      <Text mb={2}>
        • Perform the contract entered into between Schneider Electric and you in the context of the use of our
        products, services or digital content, including addressing your requests and managing accounts.
      </Text>
      <Text mb={2}>
        • Comply with legal obligations to which we are subject, for example mandatory tax and accounting obligations.
      </Text>
      <Text mb={2}>
        • Surveys, marketing communications, and interest-based advertising in our legitimate interests or with your
        consent where required.
      </Text>
      <Text mb={2}>• Use of location data with your consent where required.</Text>
      <Text mb={2}>• Other legitimate interests as described in this Privacy Policy.</Text>

      <Text fontWeight="bold">11.2 Will personal information be transferred abroad?</Text>
      <Text mb={2}>
        At Schneider Electric, teams fulfilling data processing purposes may have global roles. They can be located
        anywhere Schneider Electric operates, including outside Australia, in countries which may not have equivalent
        standards for protection of personal information.
      </Text>

      <Text fontWeight="bold">11.3 Do we build profiles and do we make automated decisions?</Text>
      <Text mb={2}>
        We may create user profiles or make automated decisions for purposes such as sales, marketing, and personalized
        experiences. Some processing relies on your consent to “customisation cookies.” You may withdraw consent at any
        time.
      </Text>

      <Text fontWeight="bold">11.4 How long will personal information be retained?</Text>
      <Text mb={2}>
        Personal information is retained as long as needed for the purposes set forth in this Privacy Policy and for any
        additional period required or permitted by law. For marketing purposes, relevant customer data is kept for three
        years after the end of the relationship or last interaction.
      </Text>

      <Text fontWeight="bold">11.5 What are your additional rights?</Text>
      <Text mb={2}>
        You may request erasure, restriction, amendment, or porting of personal information and object to its use.
        Consent can be withdrawn, but processing necessary for contracts or legitimate interests may continue.
      </Text>

      <Text fontWeight="bold">11.6 Where to ask questions and file a claim?</Text>
      <Text mb={2}>
        If you have any questions about the Schneider Electric Privacy Policy, or if you wish to enquire about any
        privacy issue, make a request for access, or lodge a complaint, please contact:
      </Text>
      <Text mb={2}>
        Privacy Officer (Legal Department)
        <br />
        Schneider Electric - Pacific Zone
        <br />
        2 Banfield Road
        <br />
        Macquarie Park NSW 2113
        <br />
        <Link color={'blue.600'} href="mailto:privacy@au.schneider-electric.com">
          privacy@au.schneider-electric.com
        </Link>
      </Text>
      <Text mt={2}>Last updated: June 17, 2025</Text>
    </Box>
  );
};
